import { useState, useEffect, useRef } from 'react';

export interface UseCallTimerReturn {
  elapsedTime: number;
  formattedTime: string;
  isWarning: boolean;
  start: () => void;
  stop: () => void;
  reset: () => void;
}

export function useCallTimer(maxDuration: number): UseCallTimerReturn {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setElapsedTime((prev) => {
          const newTime = prev + 1;
          
          // Auto-end at max duration
          if (newTime >= maxDuration) {
            stop();
            return maxDuration;
          }
          
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, maxDuration]);

  const start = () => {
    setIsRunning(true);
  };

  const stop = () => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const reset = () => {
    stop();
    setElapsedTime(0);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isWarning = elapsedTime >= maxDuration * 0.9;

  return {
    elapsedTime,
    formattedTime: formatTime(elapsedTime),
    isWarning,
    start,
    stop,
    reset,
  };
}
